import React, { useEffect, useState, useCallback, useMemo } from "react";

// Home component for displaying and managing guest reviews
import { CiCirclePlus } from "react-icons/ci";
import {
  MdOutlineMessage,
  MdOutlineMarkUnreadChatAlt,
  MdBlock,
} from "react-icons/md";

import AddGuestModal from "../../AddGuestModal/AddGuestModal";
import UpdateGuestModal from "../../UpdateGuestModal/UpdateGuestModal";
import EditGuestModal from "../../EditGuestModal/EditGuestModal";
import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { toast } from "react-toastify";
import { baseUrl } from "../../../Environments/environment";
import { useAuth } from "../../../Contexts/AuthContext/AuthContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { CiCalendarDate ,CiShare2} from "react-icons/ci";
import Edit from "../../../Assets/edit.svg";
import { useProperty } from "../../../Contexts/PropertyContext/PropertyContext";
import LoadingSpinner from "../../LoadingSpinner/LoadingSpinner";


import { useSocket } from "../../../Contexts/SocketContext/SocketContext";
import ChatScreenModal from "../Message/ChatScreenModal/ChatScreenModal";
import AddonRequestModal from "../../AddonRequestModal/AddonRequestModal";
import { apiClient } from "../../../api";
import { useSearchParams } from "react-router-dom";
import NoDataFound from "../../NoDataFound";
 import Milestone from "../../ReviewDashboard/Milestone";

dayjs.extend(utc);
dayjs.extend(timezone);

const DATE_PRESETS = [
  { id: "all", label: "All" },
  { id: "today", label: "Today" },
  { id: "yesterday", label: "Yesterday" },
  { id: "thisWeek", label: "This Week" },
  { id: "thisMonth", label: "This Month" },
  { id: "thisYear", label: "This Year" },
  { id: "custom", label: "Custom range" },
];

function getRangeForPreset(presetId) {
  const tz = dayjs.tz.guess();
  const now = dayjs().tz(tz);
  switch (presetId) {
    case "today": {
      const d = now.format("YYYY-MM-DD");
      return { from: d, to: d };
    }
    case "yesterday": {
      const y = now.subtract(1, "day");
      return { from: y.format("YYYY-MM-DD"), to: y.format("YYYY-MM-DD") };
    }
    case "thisWeek": {
      const start = now.startOf("week");
      const end = now.endOf("week");
      return { from: start.format("YYYY-MM-DD"), to: end.format("YYYY-MM-DD") };
    }
    case "thisMonth": {
      return {
        from: now.startOf("month").format("YYYY-MM-DD"),
        to: now.endOf("month").format("YYYY-MM-DD"),
      };
    }
    case "thisYear": {
      return {
        from: now.startOf("year").format("YYYY-MM-DD"),
        to: now.endOf("year").format("YYYY-MM-DD"),
      };
    }
    default: {
      const d = now.format("YYYY-MM-DD");
      return { from: d, to: d };
    }
  }
}

function filterClassNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const useReactQuerySubscription = (socket, propertyId) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (socket) {
      const updateGuests = () => {
        queryClient.invalidateQueries({ queryKey: ["guests", propertyId] });
      };

      socket.on("guest:guestStatusUpdate", updateGuests);
      socket.on("guest:guestUpdate", updateGuests);

      return () => {
        socket.off("guest:guestStatusUpdate", updateGuests);
        socket.off("guest:guestUpdate", updateGuests);
      };
    }
  }, [socket, queryClient, propertyId]);
};

const saveAuthHeaderToken = (response) => {
  if (response.ok) {
    const newToken = response.headers.get("Authorization");
    if (newToken) {
      localStorage.setItem("accessToken", newToken.split(" ")[1]);
    }
  }
};

const fetchGuests = async (
  propertyId,
  datePreset,
  searchQuery,
  accessToken,
  todaysDate,
  customRange
) => {
  try {
    const normalizedPreset = String(datePreset ?? "all").toLowerCase();
    const baseParams = {
      ...(searchQuery && { search: searchQuery }),
      todaysDate,
    };

    if (normalizedPreset === "all") {
      const response = await apiClient.get(`${baseUrl}/guest/${propertyId}`, {
        params: { ...baseParams, currentStatus: "Reservation" },
      });
      saveAuthHeaderToken(response);
      return response.data.result?.guests ?? [];
    }

    let dateRangeFrom;
    let dateRangeTo;
    if (normalizedPreset === "custom") {
      if (!customRange?.from || !customRange?.to) return [];
      dateRangeFrom = customRange.from;
      dateRangeTo = customRange.to;
    } else {
      ({ from: dateRangeFrom, to: dateRangeTo } =
        getRangeForPreset(normalizedPreset));
    }
    const fromD = dayjs(dateRangeFrom);
    const toD = dayjs(dateRangeTo);
    const dayCount = toD.diff(fromD, "day") + 1;

    if (dayCount <= 31) {
      const promises = [];
      let d = fromD;
      while (!d.isAfter(toD, "day")) {
        const selectedDate = d.format("YYYY-MM-DD");
        promises.push(
          apiClient
            .get(`${baseUrl}/guest/${propertyId}`, {
              params: { ...baseParams, selectedDate },
            })
            .then((res) => {
              saveAuthHeaderToken(res);
              return res.data.result?.guests ?? [];
            })
        );
        d = d.add(1, "day");
      }
      const lists = await Promise.all(promises);
      const byId = new Map();
      for (const list of lists) {
        for (const g of list) {
          byId.set(g._id, g);
        }
      }
      const merged = Array.from(byId.values());
      if (normalizedPreset === "today") {
        const tz = dayjs.tz.guess();
        const now = dayjs().tz(tz);
        return merged.filter((g) => {
          const t = g.review?.updatedAt || g.review?.createdAt;
          return t && dayjs(t).tz(tz).isSame(now, "day");
        });
      }
      return merged;
    }

    const response = await apiClient.get(`${baseUrl}/guest/${propertyId}`, {
      params: {
        ...baseParams,
        selectedDate: dateRangeFrom,
        dateFrom: dateRangeFrom,
        dateTo: dateRangeTo,
      },
    });
    saveAuthHeaderToken(response);
    return response.data.result?.guests ?? [];
  } catch (error) {
    toast.error("Failed to fetch guests. Please try again.");
    throw error;
  }
};

const renderStars = (rating) => {
  const full = Math.max(0, Math.min(5, Number(rating) || 0));
  return (
    <div className="flex items-center gap-1">
      {[...Array(5)].map((_, i) => (
        <span
          key={i}
          className={
            i < full ? "text-yellow-400 text-xl" : "text-gray-300 text-xl"
          }
        >
          ★
        </span>
      ))}
    </div>
  );
};

export default function Home() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditGuestModalOpen, setIsEditGuestModalOpen] = useState(false);
  const [isUpdateGuestModalOpen, setIsUpdateGuestModalOpen] = useState(false);
  const [isAddonRequestModalOpen, setIsAddonRequestModalOpen] = useState(false);
  const { propertyChats } = useProperty();

  const [searchParams] = useSearchParams();
  const openModel = searchParams.get("openAddOnsRequest");
  useEffect(() => {
    if (openModel === "true") {
      setIsAddonRequestModalOpen(true);
    }
  }, [openModel]);

  // const [guests, setGuests] = useState([]);
  const [guest, setGuest] = useState();
  const [selectedGuest, setSelectedGuest] = useState(null);
  const { selectedPropertyId } = useProperty();
  const { socket } = useSocket();
  const [searchQuery, setSearchQuery] = useState("");


  const { accessToken } = useAuth();
  const propertyId = selectedPropertyId;
  const [datePreset, setDatePreset] = useState("all");
  const [customDraftFrom, setCustomDraftFrom] = useState("");
  const [customDraftTo, setCustomDraftTo] = useState("");
  const [customRangeFrom, setCustomRangeFrom] = useState("");
  const [customRangeTo, setCustomRangeTo] = useState("");

  const dateRangeKey = useMemo(() => {
    if (datePreset === "all") return "all";
    if (datePreset === "custom") {
      if (!customRangeFrom || !customRangeTo) return "custom_pending";
      return `custom_${customRangeFrom}_${customRangeTo}`;
    }
    const { from, to } = getRangeForPreset(datePreset);
    return `${from}_${to}`;
  }, [datePreset, customRangeFrom, customRangeTo]);

  const todayDate = dayjs().tz(dayjs.tz.guess()).format("YYYY-MM-DD");
  useEffect(() => {
    const handleNewAddon = (data) => {
      // setUncheckedAddonReq((count) => count + data.count);
    };

    socket.on("addOn:newAddon", handleNewAddon);
    return () => {
      socket.off("addOn:newAddon", handleNewAddon);
    };
  }, [socket]);


  const fetchAddOns = useCallback(async () => {
    try {
      const response = await apiClient.get(
        `${baseUrl}/guest/addons/${propertyId}?requestStatus=Requested`
      );
      console.log(response);
      console.warn(response.data.result.requests);
      if (response.status === 200) {
        const newToken = response.headers.get("Authorization");
        if (newToken) {
          localStorage.setItem("accessToken", newToken.split(" ")[1]); // Save the new token
        }
        console.log("AdddOns -->>>>>>", response.data.result.requests);
        // const tempAddons = response.data.result.requests;
        // setAddOns(tempAddons);
        // setAddOnLength(tempAddons.length);
      }
    } catch (error) {
      console.error("Error fetching requests:", error);
    }
  }, [propertyId]);

  useEffect(() => {
    console.log("USE EFFECT FETCH");
    fetchAddOns();
  }, [fetchAddOns]);

  // useEffect(() => {
  //   console.log("HERE Helllooooo");
  //   console.warn(addOns);
  //   if (addOnLength !== addOns.length) {
  //     setAddOnLength(addOns.length);
  //   }
  // }, [addOns, isAddonRequestModalOpen]);

  const getInitials = (firstName, lastName) => {
    return ((firstName?.[0] ?? "") + (lastName?.[0] ?? "")).toUpperCase();
  };

  const handleSelectDatePreset = (presetId) => {
    if (presetId === "custom") {
      const tz = dayjs.tz.guess();
      const to = dayjs().tz(tz).format("YYYY-MM-DD");
      const from = dayjs().tz(tz).subtract(6, "day").format("YYYY-MM-DD");
      setCustomDraftFrom(from);
      setCustomDraftTo(to);
      setCustomRangeFrom(from);
      setCustomRangeTo(to);
    }
    setDatePreset(presetId);
  };

  const handleApplyCustomRange = () => {
    let from = customDraftFrom;
    let to = customDraftTo;
    if (!from || !to) {
      toast.warn("Please select start and end dates.");
      return;
    }
    if (dayjs(from).isAfter(dayjs(to))) {
      const swap = from;
      from = to;
      to = swap;
      setCustomDraftFrom(from);
      setCustomDraftTo(to);
    }
    setCustomRangeFrom(from);
    setCustomRangeTo(to);
  };

  const dateFilterButtonLabel = useMemo(() => {
    if (
      datePreset === "custom" &&
      customRangeFrom &&
      customRangeTo
    ) {
      return `${dayjs(customRangeFrom).format("MM/DD")} – ${dayjs(customRangeTo).format("MM/DD")}`;
    }
    return DATE_PRESETS.find((p) => p.id === datePreset)?.label ?? "All";
  }, [datePreset, customRangeFrom, customRangeTo]);

  //  console.log("searach",searchQuery)
  useReactQuerySubscription(socket, propertyId);

  const customRangeValid =
    datePreset !== "custom" ||
    (customRangeFrom &&
      customRangeTo &&
      !dayjs(customRangeFrom).isAfter(dayjs(customRangeTo)));

  const {
    data: guests = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["guests", propertyId, datePreset, dateRangeKey, searchQuery],
    queryFn: () =>
      fetchGuests(
        propertyId,
        datePreset,
        searchQuery,
        accessToken,
        todayDate,
        datePreset === "custom"
          ? { from: customRangeFrom, to: customRangeTo }
          : null
      ),
    enabled: Boolean(propertyId) && customRangeValid,
    staleTime: 0,
    gcTime: 600000,
  });

  const normalizedGuests = useMemo(() => {
    if (Array.isArray(guests)) return guests;
    if (Array.isArray(guests?.data)) return guests.data;
    return [];
  }, [guests]);

  useEffect(() => {
    if (!Array.isArray(normalizedGuests)) {
      console.error("Home guests is not an array", {
        guests,
        normalizedGuests,
        type: typeof guests,
      });
    }
  }, [guests, normalizedGuests]);

  const openChatScreen = (guest) => {
    setSelectedGuest(guest);
  };

  const closeChatScreen = () => {
    setSelectedGuest(null);
  };

  const openUpdateGuestModal = () => {
    setIsUpdateGuestModalOpen(true);
  };

  // Function to close the Update Guest modal
  const closeUpdateGuestModal = () => {
    setIsUpdateGuestModalOpen(false);
  };

  // Function to open AddonRequestModal when bell icon is clicked

  const shareReviewLink = async (e) => {
    e.stopPropagation();
  
    const reviewUrl = `https://customer.onelyk.com/rating/${propertyId}`;
  
    try {
      if (navigator.share) {
        await navigator.share({
          title: "Review Request",
          text: "Please share your feedback",
          url: reviewUrl,
        });
      } else {
        await navigator.clipboard.writeText(reviewUrl);
        alert("Link copied to clipboard");
      }
    } catch (error) {
      console.error("Failed to share review link:", error);
    }
  };

  if (error) return <p>Error fetching guests.</p>;

  return (
    <>
      {isLoading && <LoadingSpinner />}
      <div className="px-2 sm:px-4 lg:px-6">
        <Milestone />
        {/* Header Section - Responsive Layout */}
        <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between mt-2 sm:mt-4">
          {/* Date filter: presets + optional custom range */}
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-start sm:gap-3">
            <Menu as="div" className="relative inline-block text-left">
              <MenuButton className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-900 shadow-sm hover:border-indigo-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1">
                <CiCalendarDate
                  className="text-gray-500 text-xl sm:text-2xl shrink-0"
                  aria-hidden
                />
                <span className="whitespace-nowrap max-w-[200px] sm:max-w-none truncate">
                  {dateFilterButtonLabel}
                </span>
              </MenuButton>
              <MenuItems
                transition
                className="absolute left-0 z-50 mt-2 w-48 origin-top-left rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
              >
                {DATE_PRESETS.map((preset) => (
                  <MenuItem
                    key={preset.id}
                    as="button"
                    type="button"
                    onClick={() => handleSelectDatePreset(preset.id)}
                    className={filterClassNames(
                      "block w-full px-4 py-2 text-left text-sm focus:outline-none focus:bg-indigo-50",
                      datePreset === preset.id
                        ? "text-indigo-600 font-medium"
                        : "text-gray-700"
                    )}
                  >
                    {preset.label}
                  </MenuItem>
                ))}
              </MenuItems>
            </Menu>
            {datePreset === "custom" && (
              <div className="flex flex-wrap items-end gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-sm">
                <div className="flex flex-col gap-0.5">
                  <label
                    htmlFor="custom-range-from"
                    className="text-xs font-medium text-gray-600"
                  >
                    From
                  </label>
                  <input
                    id="custom-range-from"
                    type="date"
                    value={customDraftFrom}
                    onChange={(e) => setCustomDraftFrom(e.target.value)}
                    className="rounded-md border border-gray-300 px-2 py-1.5 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </div>
                <div className="flex flex-col gap-0.5">
                  <label
                    htmlFor="custom-range-to"
                    className="text-xs font-medium text-gray-600"
                  >
                    To
                  </label>
                  <input
                    id="custom-range-to"
                    type="date"
                    value={customDraftTo}
                    onChange={(e) => setCustomDraftTo(e.target.value)}
                    className="rounded-md border border-gray-300 px-2 py-1.5 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleApplyCustomRange}
                  className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1"
                >
                  Apply
                </button>
              </div>
            )}
          </div>

          {/* Search and Button Section */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
            {/* Search Bar */}
            <div className="flex-1 sm:flex-initial sm:w-64 lg:w-80">
              <label htmlFor="search" className="sr-only">
                Search
              </label>
              <div className="relative text-gray-400 focus-within:text-gray-600">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <MagnifyingGlassIcon aria-hidden="true" className="h-4 w-4 sm:h-5 sm:w-5" />
                </div>
                <input
                  id="search"
                  name="search"
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search Request"
                  className="block w-full rounded-xl sm:rounded-2xl border-0 bg-gray-100 py-2 sm:py-1.5 pl-9 sm:pl-10 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-colors"
                />
              </div>
            </div>

            {/* Request Review Button */}
            <div className="flex gap-2">
            <button
              type="button"
              className="w-full inline-flex items-center justify-center gap-x-1 rounded-md bg-indigo-600 px-3 sm:px-3.5 py-2 sm:py-2.5 text-center text-xs sm:text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-colors whitespace-nowrap"
              onClick={() => setIsModalOpen(true)}
            >
              <CiCirclePlus className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
              <span className="hidden sm:inline">Request Review</span>
              <span className="sm:hidden">Review</span>
            </button>   
<button
              type="button"
              className="md:hidden  w-full inline-flex items-center justify-center gap-x-1 rounded-md bg-indigo-600 px-3 sm:px-3.5 py-2 sm:py-2.5 text-center text-xs sm:text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-colors whitespace-nowrap"
              onClick={(e) => shareReviewLink(e)}
            >
            <CiShare2 className="w-5 h-5" />
             <span >Share Review Link</span>
            </button> 
            </div>
        
          </div>
        </div>

        {/* Guests Display - Table for Desktop, Cards for Mobile */}
        <div className="mt-4 sm:mt-6">
          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <div className="inline-block min-w-full align-middle">
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-[#F9FAFB]">
                    <tr>
                      <th
                        scope="col"
                        className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900"
                      >
                        Name
                      </th>
                      <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Rating
                    </th>
                    {/* <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                    Date
                    </th> */}
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Date & Time
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Email
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Phone
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {Array.isArray(normalizedGuests) && normalizedGuests.length > 0 ? normalizedGuests.map((guest) => (
                      <tr key={guest._id} className="hover:bg-gray-50 transition-colors">
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm">
                          <div className="flex items-center">
                            <div
                              className={`h-10 w-10 flex-shrink-0 flex items-center justify-center rounded-full ${
                                guest?.review?.overAllRating>0
                                ? "bg-green-500"
                                : guest.msgSendFailed
                                ? "bg-red-500"
                                : "bg-[#6B7280]"
                              } text-white font-medium`}
                            >
                              {guest.msgSendFailed ? (
                                <MdBlock className="text-xl" title="Message Send Failed" />
                              ) : (
                                getInitials(guest?.firstName, guest?.lastName)
                              )}
                            </div>
                            <div className="ml-4">
                              <div className="font-medium text-gray-900">
                                {(guest?.firstName ?? "") +
                                  " " +
                                  (guest?.lastName ?? "")}
                              </div>
                            </div>
                          </div>
                        </td>
                        {guest?.review?.overAllRating ? (
                        <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-700 font-semibold ">
                              {Number(guest?.review?.overAllRating)}
                            </span>{" "}
                            {renderStars(guest?.review?.overAllRating)}
                          </div>
                        </td>
                      ) : (
                        <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">
                       Not Review Yet
                        </td>
                      )}
                            {/* {guest?.review?.updatedAt ? (
                        <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">
                          <p>
                            {" "}
                            {new Date(guest?.review?.updatedAt).toLocaleString("en", {
                              day: "2-digit",
                              month: "2-digit",
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true,

                              timeZone: "UTC",
                            })}
                          </p>
                        </td>
                      ) : (
                        <td className="whitespace-nowrap px-3 py-5 space-y-1 text-sm text-gray-500">
                     
                        </td>
                      )} */}
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {guest?.createdAt || guest?.review?.updatedAt ? (
                            <div className="flex flex-col">
                              <span className="text-gray-900 font-medium">{dayjs(guest?.createdAt || guest?.review?.updatedAt).format("DD MMM YYYY")}</span>
                              <span className="text-xs text-gray-500">{dayjs(guest?.createdAt || guest?.review?.updatedAt).format("hh:mm A")}</span>
                            </div>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          <div className="max-w-xs truncate" title={guest.email}>
                            {guest.email}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {guest.phoneNumber ? (
                            <>
                              {guest.countryCode} {guest.phoneNumber}
                            </>
                          ) : (
                            <span className="text-red-500">No phone number</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          <div className="flex items-center gap-3">
                          {/* {!guest.isReview ?(       <button
                              className="text-indigo-600 hover:text-indigo-900 transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                openUpdateGuestModal(guest);
                                setGuest(guest);
                              }}
                              aria-label="Edit guest"
                            >
                              <img src={Edit} alt="Edit" className="w-5 h-5" />
                            </button>):(<></>)} */}
                     
                            {!guest.isReview ?(
                            <button
                              className="relative text-indigo-600 hover:text-indigo-900 transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                openChatScreen(guest);
                              }}
                              aria-label="Open chat"
                            >
                              {propertyChats?.chatLists?.find(
                                (chat) =>
                                  chat.guestId === guest._id &&
                                  chat.unreadMessages > 0
                              ) ? (
                                <MdOutlineMarkUnreadChatAlt
                                  color="#4f46e5"
                                  size={22}
                                />
                              ) : (
                                <MdOutlineMessage color="#4f46e5" size={22} />
                              )}
                              {propertyChats?.chatLists?.find(
                                (chat) =>
                                  chat.guestId === guest._id &&
                                  chat.unreadMessages > 0
                              )?.unreadMessages && (
                                <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                                  {propertyChats?.chatLists?.find(
                                    (chat) =>
                                      chat.guestId === guest._id &&
                                      chat.unreadMessages > 0
                                  ).unreadMessages > 99
                                    ? "99+"
                                    : propertyChats?.chatLists?.find(
                                        (chat) =>
                                          chat.guestId === guest._id &&
                                          chat.unreadMessages > 0
                                      ).unreadMessages}
                                </span>
                              )}
                            </button>
):(<></>)}
                          </div>
                        </td>
                      </tr>
                    )): (
                      <>
                        {!isLoading && (
                          <tr>
                            <td colSpan={6} className="py-10">
                              <NoDataFound message={"No reviews found"}/>
                            </td>
                          </tr>
                        )}
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-3">
            {Array.isArray(normalizedGuests) && normalizedGuests.length > 0 ? (
              normalizedGuests.map((guest) => (
                <div
                  key={guest._id}
                  className={`bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow ${
                    !guest.isReview ? "cursor-pointer" : ""
                  }`}
                  onClick={() => {
                    if (!guest.isReview) {
                      openChatScreen(guest);
                    }
                  }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <div
                        className={`h-12 w-12 flex-shrink-0 flex items-center justify-center rounded-full ${
                          guest.msgSendFailed
                            ? "bg-red-500"
                            : "bg-[#6B7280]"
                        } text-white font-medium`}
                      >
                        {guest.msgSendFailed? (
                          <span className="text-xl">X</span>
                        ) : (
                          getInitials(guest?.firstName, guest?.lastName)
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        {/* Row 1: Name and Date */}
                        <div className="flex justify-between items-start gap-2">
                          <div className="font-semibold text-gray-900 text-sm sm:text-base truncate">
                            {(guest?.firstName ?? "") +
                              " " +
                              (guest?.lastName ?? "")}
                          </div>
                          <div className="text-xs text-gray-700 font-medium flex-shrink-0">
                            {dayjs(guest?.createdAt || guest?.review?.updatedAt).format("DD MMM YYYY")}
                          </div>
                        </div>
                        
                        {/* Row 2: Mobile and Time */}
                        <div className="mt-1 flex justify-between items-center gap-2">
                          <div className="text-xs sm:text-sm text-gray-500 truncate">
                            {guest.phoneNumber ? (
                              <>
                                {guest.countryCode} {guest.phoneNumber}
                              </>
                            ) : (
                              <span className="text-red-500">No phone number</span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 flex-shrink-0">
                            {dayjs(guest?.createdAt || guest?.review?.updatedAt).format("hh:mm A")}
                          </div>
                        </div>

                        {/* Row 3: Email */}
                        <div className="mt-1 text-xs sm:text-sm text-gray-500 truncate">
                          {guest.email}
                        </div>
                        <div className="mt-1 text-xs sm:text-sm text-gray-500 truncate">
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-700 font-semibold ">
                              {Number(guest?.review?.overAllRating)}
                            </span>{" "}
                            {renderStars(guest?.review?.overAllRating)}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                      {!guest.isReview ? (
                        <button
                          className="relative p-2 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-full transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            openChatScreen(guest);
                          }}
                          aria-label="Open chat"
                        >
                          {propertyChats?.chatLists?.find(
                            (chat) =>
                              chat.guestId === guest._id &&
                              chat.unreadMessages > 0
                          ) ? (
                            <MdOutlineMarkUnreadChatAlt
                              color="#4f46e5"
                              size={22}
                            />
                          ) : (
                            <MdOutlineMessage color="#4f46e5" size={22} />
                          )}
                          {propertyChats?.chatLists?.find(
                            (chat) =>
                              chat.guestId === guest._id &&
                              chat.unreadMessages > 0
                          )?.unreadMessages && (
                            <span className="absolute top-0 right-0 bg-red-600 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                              {propertyChats?.chatLists?.find(
                                (chat) =>
                                  chat.guestId === guest._id &&
                                  chat.unreadMessages > 0
                              ).unreadMessages > 99
                                ? "99+"
                                : propertyChats?.chatLists?.find(
                                    (chat) =>
                                      chat.guestId === guest._id &&
                                      chat.unreadMessages > 0
                                  ).unreadMessages}
                            </span>
                          )}
                        </button>
                      ) : (
                        <></>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              !isLoading && <NoDataFound message={"No reviews found"} />
            )}
          </div>
        </div>
        {/* Add Guest Modal */}
        <AddGuestModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />

        {/* Render the Update Guest Modal */}
        {isUpdateGuestModalOpen && (
          <UpdateGuestModal
            guest={guest}
            onClose={closeUpdateGuestModal} // Close modal function
          />
        )}

        {guest && (
          <EditGuestModal
            isOpen={isEditGuestModalOpen}
            onClose={() => setIsEditGuestModalOpen(false)}
            guest={guest}
          />
        )}

        {selectedGuest && (
          <ChatScreenModal
            guestId={selectedGuest._id}
            guest={selectedGuest}
            onClose={closeChatScreen}
          />
        )}

        {isAddonRequestModalOpen && (
          <AddonRequestModal
            propertyId={propertyId}
            onClose={() => {
              setIsAddonRequestModalOpen(false);
              fetchAddOns();
            }}
          />
        )}
      </div>
    </>
  );
}
